import React, { useEffect, useState } from "react";
import { Ban, CreditCard, FileSignature, MoreHorizontal, Pause, Pencil, Play, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Contract } from "../../features/contracts/models/Contract";
import { ContractAcceptanceStatus } from "../../features/contracts/models/ContractAcceptanceStatus";
import { ContractStatus } from "../../features/contracts/models/ContractStatus";

interface ContractRowActionsProps {
  contract: Contract;
  onAddPayment: () => void;
  onShowPayments: () => void;
  onEdit: () => void;
  onHold: () => void;
  onResume: () => void;
  onCancel: () => void;
  onRequestAcceptance: () => void;
}

const itemClass =
  "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20";

/**
 * A contract row's actions: the primary one (Add payment) stays visible; the
 * rest collapse into a kebab menu so rows stay scannable.
 */
const ContractRowActions: React.FC<ContractRowActionsProps> = ({
  contract, onAddPayment, onShowPayments, onEdit, onHold, onResume, onCancel, onRequestAcceptance,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest("[data-contract-menu]")) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className="relative inline-flex items-center gap-1" data-contract-menu>
      {contract.status === ContractStatus.Active && (
        <button
          type="button"
          onClick={onAddPayment}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-grey-90 dark:text-primary-90 dark:hover:bg-primary-20"
        >
          <CreditCard size={14} />
          <span>{t("billing.actions.addPayment")}</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-md p-1 text-grey-30 hover:bg-grey-90 dark:text-grey-70 dark:hover:bg-primary-20"
        title={t("billing.rowActions.menuTitle")}
      >
        <MoreHorizontal size={16} />
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-20 w-44 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          <button type="button" className={itemClass} onClick={pick(onShowPayments)}>
            <Receipt size={14} />
            <span>{t("billing.rowActions.payments")}</span>
          </button>
          <button type="button" className={itemClass} onClick={pick(onEdit)}>
            <Pencil size={14} />
            <span>{t("billing.rowActions.edit")}</span>
          </button>
          {contract.status === ContractStatus.Active && (
            <button type="button" className={itemClass} onClick={pick(onHold)}>
              <Pause size={14} />
              <span>{t("billing.rowActions.hold")}</span>
            </button>
          )}
          {contract.status === ContractStatus.OnHold && (
            <button type="button" className={itemClass} onClick={pick(onResume)}>
              <Play size={14} />
              <span>{t("billing.rowActions.resume")}</span>
            </button>
          )}
          {contract.status !== ContractStatus.Cancelled &&
            contract.acceptanceStatus !== ContractAcceptanceStatus.Accepted && (
            <button type="button" className={itemClass} onClick={pick(onRequestAcceptance)}>
              <FileSignature size={14} className="shrink-0" />
              <span>
                {contract.acceptanceStatus === ContractAcceptanceStatus.Pending
                  ? t("billing.rowActions.resendAcceptance", "Resend request")
                  : t("billing.rowActions.requestAcceptance", "Request acceptance")}
              </span>
            </button>
          )}
          {contract.status !== ContractStatus.Cancelled && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
              onClick={pick(onCancel)}
            >
              <Ban size={14} />
              <span>{t("billing.actions.cancelContract")}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContractRowActions;
